<script lang="ts">
import Vue from 'vue'
import { ActionBarButton, ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { ContextName, ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { Action, Getter, Mutation } from 'vuex-class'
import { Icons } from '@/icons/icons'
import TabsView from '@/views/Tabs/TabsView.vue'
import { Component } from 'vue-property-decorator'
import { DialogTypes } from '@/store/modules/dialog/dialogTypes'
import {
  InvoicesFormFields,
  InvoiceTabName,
  RenderedForm
} from '@/components/billing/InvoicesTab/types/InvoicesTabComponentTypes'
import BillingMixin from '@/mixins/BillingMixin.vue'
import { AlertsTypes, ComponentWhereIsRendered } from '@/store/modules/alerts/alertsTypes'
import { TranslateResult } from 'vue-i18n'
import { PrintInvoiceTypes } from '@/store/modules/invoices/invoicesTypes'
import { URLS } from '@/router/routes/urlRoutes'
import { printInvoice } from '@/helpers/invoice'
import { ITemplate } from '@/store/modules/template/templateTypes'
import { ExpedientCustomerFact } from '@/store/modules/expedients/expedientsTypes'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import { propertyExist } from '@/helpers/object'
import { FacturaEStatusEnum, IFacturaEForUpdate } from '@/store/modules/facturaE/facturaETypes'
import { getBoolean, isTrue } from '@/helpers/boolean'

const alertsModule = ModuleNamespaces.ALERTS
const authModule = ModuleNamespaces.AUTH
const configurationModule = ModuleNamespaces.CONFIGURATION
const dialogModule = ModuleNamespaces.DIALOG
const expedientsModule = ModuleNamespaces.EXPEDIENTS
const formsModule = ModuleNamespaces.FORMS
const invoicesModule = ModuleNamespaces.INVOICES
const notificationModule = ModuleNamespaces.NOTIFICATIONS
const templateModule = ModuleNamespaces.TEMPLATE
const selectedRegisterModule = ModuleNamespaces.SELECTED_REGISTER
const facturaEModule = ModuleNamespaces.FACTURA_E

@Component({
  mixins: [BillingMixin]
})
export default class BillingDetailView extends TabsView {
  @Getter('getInvoiceType', { namespace: invoicesModule })
  invoiceType: () => string

  @Getter('getTemplateTree', { namespace: templateModule })
  tree: ITemplate[]

  @Getter('getCompanyTemplateInvoices', { namespace: authModule })
  hasCompanyTemplateInvoice: boolean

  @Getter('getRegisterFormData', { namespace: formsModule })
  getFormData: () => object

  @Getter('showAditionalForm', { namespace: formsModule })
  showAditionalForm: (context: string) => boolean

  @Getter('getExpedientCustomerFact', { namespace: expedientsModule })
  expedientCustomerFactGetter: ExpedientCustomerFact | null

  @Getter('isFacturaEIssued', { namespace: selectedRegisterModule })
  isFacturaEIssued: () => boolean

  @Getter('getIsFactureUser', { namespace: authModule })
  isFactureUser: boolean

  @Action('sendMailNotification', { namespace: notificationModule })
  sendInvoiceNotification: ({}) => Promise<void>

  @Action('printPdfInvoice', { namespace: invoicesModule })
  printInvoiceAction: ({}) => Promise<void>

  @Action('showDialog', { namespace: dialogModule })
  showDialogAction: ({}) => void

  @Action('showAlert', { namespace: alertsModule })
  showAlert: ({}) => {}

  @Action('convertProformaToCustomerInvoice', { namespace: invoicesModule })
  convertProformaToCustomerInvoice: (idInvoice: number) => Promise<string>

  @Action('generateTemplate', { namespace: templateModule })
  generateTemplate: ({}) => void

  @Action('updateFacturaE', { namespace: facturaEModule })
  updateFacturaE: (facturaE: IFacturaEForUpdate) => Promise<boolean>

  @Action('setRouteFromName', { namespace: configurationModule })
  setRouteFromName: (routeFromName: string | undefined) => Promise<void>

  @Action('rectifyCustomerInvoice', { namespace: invoicesModule })
  rectifyCustomerInvoiceAction: ({}) => Promise<string>

  @Mutation('CLEAR_FORM_IS_VALID', { namespace: formsModule })
  clearFormIsValid: (context: string) => void

  @Mutation('RESET_EXPEDIENT_CUSTOMER_FACT', { namespace: expedientsModule })
  resetExpedientCustomerFactMutation: () => void

  breadcrumbText: TranslateResult = ''

  invoiceSendAvailable = [
    ContextName.BILLING_CUSTOMER_INVOICES,
    ContextName.BILLING_PROFORMA_INVOICES,
    ContextName.BILLING_PROVISIONS
  ]

  renderedInvoiceForm = ''

  get buttons() {
    const { alias, type } = this.entity(this.getContext)
    const { canSave, canDelete } = this.checkEntityPermissionsGetter(type)

    let buttons = []

    if (this.isFacturaEIssuedView) {
      buttons = [
        {
          icon: Icons.CLOSE,
          tooltip: this.$t('action_buttons.cancel'),
          class: 'red-color',
          action: ActionName.CLOSE,
          enabled: true,
          variant: LabelButtonVariant.OUTLINED
        },
        {
          icon: Icons.CHECK,
          tooltip: this.$t('action_buttons.save'),
          class: 'green-color',
          action: ActionName.SAVE_FACTURA_E,
          enabled: true,
          hidden: !canSave
        },
        ...this.buildSendEmailItem(),
        ...this.buildRectifyInvoiceItem(),
        {
          icon: Icons.DOCUMENT_VALIDATE,
          tooltip: this.setPrintText,
          action: ActionName.PRINT,
          enabled: this.showPrintButton,
          hidden: !Object.values(PrintInvoiceTypes).includes(alias as PrintInvoiceTypes),
          isContextMenuOption: true
        }
      ]
    } else {
      buttons = [
        {
          icon: Icons.COIN,
          tooltip: this.$t('action_buttons.convert_to_customer_invoice'),
          action: ActionName.CONVERT_TO_CUSTOMER_INVOICE,
          enabled: this.enableConvertToCustomerInvoiceButton,
          hidden: !this.isProformaInvoicesTabView || !canSave,
          isContextMenuOption: true
        },
        {
          icon: Icons.DOCUMENT_VALIDATE,
          tooltip: this.setPrintText,
          action: ActionName.PRINT,
          enabled: this.showPrintButton,
          hidden: !Object.values(PrintInvoiceTypes).includes(alias as PrintInvoiceTypes),
          isContextMenuOption: true
        },
        {
          icon: Icons.DOCUMENT_VALIDATE,
          tooltip: this.$t('components.context_menu.send_facturae'),
          action: ActionName.SEND_FACTURA_E,
          enabled: this.showSendEmailButton,
          hidden: !this.isCustomerInvoiceTabView,
          isContextMenuOption: true
        },
        ...this.buildSendEmailItem(),
        ...this.buildRectifyInvoiceItem(),
        ...(this.isFacturaEStatusError
          ? []
          : [
              {
                icon: Icons.REMOVE,
                tooltip: this.$t('action_buttons.remove'),
                action: ActionName.REMOVE,
                enabled: this.showAditionalForm(this.getContext) || !this.buttonsEnabled ? false : true,
                hidden: !this.selectedInvoiceId || !canDelete,
                isContextMenuOption: true
              }
            ]),
        {
          icon: Icons.CLOSE,
          tooltip: this.$t('action_buttons.cancel'),
          class: 'red-color',
          action: ActionName.CLOSE,
          enabled: this.showAditionalForm(this.getContext) || !this.buttonsEnabled ? false : true,
          variant: LabelButtonVariant.OUTLINED
        },
        {
          icon: Icons.CHECK,
          tooltip: this.$t('action_buttons.save'),
          class: 'green-color',
          action: ActionName.SAVE,
          enabled: !this.checkIfFormIsValid(this.renderedInvoiceForm) || !this.buttonsEnabled ? false : true,
          hidden: !canSave
        }
      ]
    }

    return buttons.filter((button: ActionBarButton) => !button.hidden)
  }

  get enableConvertToCustomerInvoiceButton(): boolean {
    const register = this.selectedRegisterData(this.context)
    return (
      Number(register.billed) === 0 &&
      Number(register.id) > 0 &&
      (this.showAditionalForm(this.getContext) || this.buttonsEnabled)
    )
  }

  get isFacturaEIssuedView(): boolean {
    if (this.isCustomerInvoiceTabView || this.isCustomerBankdraftsTabView) {
      return this.isFacturaEIssued()
    }
    return false
  }

  get isFacturaEStatusError(): boolean {
    if (this.isCustomerInvoiceTabView || this.isCustomerBankdraftsTabView) {
      return (
        FacturaEStatusEnum.ERROR.toLowerCase() ===
        this.selectedRegisterData(this.context)?.statusFacturaE?.toLowerCase()
      )
    }
    return false
  }

  get isCustomerInvoiceTabView(): boolean {
    return this.context === ContextName.BILLING_CUSTOMER_INVOICES
  }

  get isProformaInvoicesTabView(): boolean {
    return this.context === ContextName.BILLING_PROFORMA_INVOICES
  }

  get isProviderInvoicesTabView(): boolean {
    return this.context === ContextName.BILLING_PROVIDER_INVOICES
  }

  get isCustomerBankdraftsTabView(): boolean {
    return this.context === ContextName.BILLING_CUSTOMER_BANKDRAFTS
  }

  get isProviderBankdraftsTabView(): boolean {
    return this.context === ContextName.BILLING_PROVIDER_BANKDRAFTS
  }

  get selectedInvoiceId() {
    return this.selectedRegisterId(this.context)
  }

  // @note: alias for template
  get getSelectedInvoiceId() {
    return this.selectedInvoiceId
  }

  get getSelectedInvoiceDescription(): string {
    return this.getSelectedRegisterData.description
  }

  get hasProfessionalFacturaETemplate(): boolean {
    return isTrue(this.getSelectedRegisterData?.professionalConfig?.facturaETemplate)
  }

  get showPrintButton() {
    return this.showAditionalForm(this.getContext) ||
      !this.buttonsEnabled ||
      !this.checkIfFormIsValid(this.renderedInvoiceForm)
      ? false
      : true
  }

  get showRectifyButton() {
    return this.showPrintButton
  }

  get showSendEmailButton() {
    return this.showAditionalForm(this.getContext) ||
      this.getContext.includes((this as any).invoiceSendAvailable) ||
      !this.buttonsEnabled ||
      !this.checkIfFormIsValid(this.renderedInvoiceForm)
      ? false
      : true
  }

  get setDialogText() {
    switch (this.context) {
      case ContextName.BILLING_CUSTOMER_BANKDRAFTS:
        return this.$t('components.dialog.customers_bankdraft_literal')
      case ContextName.BILLING_PROVISIONS:
        return this.$t('components.dialog.provisions_literal')
      case ContextName.BILLING_PROVIDER_BANKDRAFTS:
        return this.$t('components.dialog.providers_bankdraft_literal')
      case ContextName.BILLING_ORDER_FORMS:
        return this.$t('components.dialog.order_form_literal')
      case ContextName.BILLING_PROFORMA_INVOICES:
        return this.$t('components.dialog.proforma_invoices_literal')
      default:
        return this.$t('components.dialog.invoices_literal')
    }
  }

  get setPrintText() {
    switch (this.context) {
      case ContextName.BILLING_PROVISIONS:
        return this.$t('components.context_menu.generate_provision')
      case ContextName.BILLING_ORDER_FORMS:
        return this.$t('components.context_menu.generate_order_form')
      default:
        return this.$t('components.context_menu.generate_invoice')
    }
  }

  get selectedRegisterInfo() {
    return {
      dialogText: this.setDialogText
    }
  }

  async onInitForm(params: any) {
    const { formData, isNewForm, schema } = params
    const register = this.selectedRegisterData(this.context)

    this.disableFieldDateLogic(formData, schema, InvoicesFormFields.CHARGED, InvoicesFormFields.CHARGE_DATE)
    this.disableFieldDateLogic(formData, schema, InvoicesFormFields.PAID, InvoicesFormFields.PAYMENT_DATE)
    if (isNewForm) {
      formData.userId = String(this.getUserId)
    }

    const showSageExportedAlert =
      this.isCustomerBankdraftsTabView && isTrue(register?.exportado_sage)
    if (showSageExportedAlert) {
      this.showFieldByName(schema, InvoicesFormFields.SAGE_EXPORTED_ALERT)
    } else {
      this.hideFieldByName(schema, InvoicesFormFields.SAGE_EXPORTED_ALERT)
    }
    this.formLoaded = true
    this.isNewRegister = isNewForm
  }

  disableFieldDateLogic(formData: any, schema: any[], fieldName: string, dateField: string): void {
    if (!propertyExist(formData, fieldName) || String(formData[fieldName]) === '1') {
      return
    }
    const inputField = schema.find((item: any) => item.id === dateField) as any
    inputField.disabled = true
    inputField.validationRules = null
    formData[dateField] = null
  }

  showFieldByName(schema: any[], fieldName: string): void {
    const field = schema.find((item: any) => item.id === fieldName) as any
    if (field) {
      field.hidden = false
    }
  }

  hideFieldByName(schema: any[], fieldName: string): void {
    const field = schema.find((item: any) => item.id === fieldName) as any
    if (field) {
      field.hidden = true
    }
  }

  async mounted() {
    this.breadcrumbText = this.setNewRegisterBreadcrumbLiteral()
    await this.$nextTick()
    this.setRouteFromName(this.routeFromName)
  }

  beforeDestroy() {
    this.resetExpedientCustomerFactMutation()
  }

  destroyed() {
    this.clearFormIsValid(this.context)
  }

  executeAction(actionName: string) {
    switch (actionName) {
      case ActionName.CLOSE:
        return this.close()
      case ActionName.SAVE_FACTURA_E:
        return this.saveFacturaE()
      case ActionName.SAVE:
        return this.save()
      case ActionName.REMOVE:
        return this.remove()
      case ActionName.SEND_EMAIL:
        return this.sendEmailDialog()
      case ActionName.PRINT:
        return this.showTemplateSelectDialog()
      case ActionName.CONVERT_TO_CUSTOMER_INVOICE:
        return this.showDialogConvertToCustomerInvoice()
      case ActionName.SEND_FACTURA_E:
        return this.renderNewFacturaEDialog()
      case ActionName.RECTIFY_CUSTOMER_INVOICE:
        return this.showDialogRectifyInvoice()
    }
  }

  onRenderForm(_tabIndex: number, contextName: string) {
    if (this.showSpinnerLayer) {
      this.hideSpinnerLayerAction()
      this.checkIfComesFromExpedient()
    }
    switch (contextName) {
      case InvoiceTabName.CUSTOMER_GENERAL_DATA:
        if (this.isFacturaEIssuedView) {
          this.renderedInvoiceForm = ContextName.FACTURA_E_ISSUED_GENERAL_DATA_FORM
        } else {
          this.renderedInvoiceForm = RenderedForm.CUSTOMER_GENERAL_DATA_FORM
        }
        break
      case InvoiceTabName.PROFORMA_GENERAL_DATA:
        this.renderedInvoiceForm = RenderedForm.PROFORMA_GENERAL_DATA_FORM
        break
      case InvoiceTabName.PROVIDER_GENERAL_DATA:
        this.renderedInvoiceForm = RenderedForm.PROVIDER_GENERAL_DATA_FORM
        break
      case InvoiceTabName.CUSTOMER_BANKDRAFTS:
        this.renderedInvoiceForm = RenderedForm.CUSTOMER_BANKDRAFTS_FORM
        break
      case InvoiceTabName.PROVIDER_BANKDRAFTS:
        this.renderedInvoiceForm = RenderedForm.PROVIDER_BANKDRAFTS_FORM
        break
      case InvoiceTabName.PROFORMA_PAYMENT_METHODS:
        this.renderedInvoiceForm = RenderedForm.PROFORMA_PAYMENT_METHODS_FORM
        break
      case ContextName.BILLING_CUSTOMER_BANKDRAFTS:
        this.renderedInvoiceForm = ContextName.BILLING_CUSTOMER_BANKDRAFTS
        break
      case ContextName.BILLING_PROVIDER_BANKDRAFTS:
        this.renderedInvoiceForm = ContextName.BILLING_PROVIDER_BANKDRAFTS
        break
      case ContextName.BILLING_PROVISIONS:
        this.renderedInvoiceForm = ContextName.BILLING_PROVISIONS
        break
      case ContextName.BILLING_ORDER_FORMS:
        this.renderedInvoiceForm = ContextName.BILLING_ORDER_FORMS
        break
    }
  }

  async sendEmailDialog() {
    await this.save(false)
    await this.loadSelectedData()
    this.showDialog({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.send_invoce_email'),
      mainButtonText: this.$t('action_buttons.send_notification'),
      action: this.sendEmail
    })
  }

  async renderNewFacturaEDialog(): Promise<void> {
    if (!this.isCustomerInvoiceTabView) {
      return
    }
    await this.saveSafe(false)

    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.facturae_prompt', { description: this.getSelectedInvoiceDescription }),
      action: async () => {
        this.facturaEDialogKey += 1
        this.showFacturaEDialog = true
      },
      mainButtonText: this.$t('action_buttons.issue_and_send'),
      secondaryButtonText: this.$t('action_buttons.cancel')
    })
  }

  async saveFacturaE() {
    const customerInvoice: any = this.getFormData
    const success = await this.updateFacturaE({
      invoiceId: customerInvoice.id,
      invoicePaid: getBoolean(customerInvoice.charged),
      portalPublish: getBoolean(customerInvoice.publish)
    })
    if (success) {
      this.close()
    }
  }

  // @note: mantiene el registro guardado en el store
  async saveSafe(redirectToGridTable = true, resolve: any = null, _idObjectType: string = '') {
    ;(this as any).regularizeFieldsOnSave()
    await this.saveSafeRegisterFormData({
      endpoint: this.endpoints(this.entity(this.getContext).type).save,
      idSelectedRegister: this.entity(this.getContext).id,
      goToURL: redirectToGridTable ? this.reviewRedirect() : null,
      context: this.getContext
    })

    if (resolve) {
      resolve()
    }
  }

  async save(redirectToGridTable = true, resolve: any = null, _idObjectType: string = '') {
    ;(this as any).regularizeFieldsOnSave()
    await this.saveRegisterFormData({
      endpoint: this.endpoints(this.entity(this.getContext).type).save,
      idSelectedRegister: this.entity(this.getContext).id,
      goToURL: redirectToGridTable ? this.reviewRedirect() : null,
      context: this.getContext
    })

    if (resolve) {
      resolve()
    }
  }

  async sendEmail() {
    const { idCustomer, id, numberInvoice } = this.selectedRegisterData(this.getContext)
    await this.sendInvoiceNotification({
      templateType: this.getContext,
      notificationData: {
        idCustomer,
        idInvoice: id,
        numberInvoice
      }
    })
  }

  async showTemplateSelectDialog() {
    await this.saveSafe(false)
    if (this.hasCompanyTemplateInvoice) {
      this.showTemplateDialog = this.checkIfOnlyOneInvoice()
    } else {
      this.showPDFDialog()
    }
  }

  showPDFDialog() {
    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.print_pdf_info'),
      mainButtonText: this.$t('action_buttons.accept'),
      hideSecondaryButton: true,
      action: this.printInvoice
    })
  }

  printInvoice() {
    const id = this.selectedInvoiceId

    this.printInvoiceAction({
      idInvoice: id,
      invoiceType: this.entity(this.getContext).alias
    })
      .then((response: any) => {
        const url = window.URL.createObjectURL(response.data)
        window.open(url)
      })
      .catch(() => {
        this.showDialogAction({
          type: DialogTypes.ERROR,
          message: this.$t('errors.generic_error')
        })
      })
  }

  onFieldChanged(schema: any, formData: any, field: any, value: any) {
    // Charged date field logic when is charged - Billing mixin
    if (field.id === InvoicesFormFields.CHARGED) {
      ;(this as any).chargedSwitchLogic(schema, formData, field, value)
    }

    // Payment date field logic when is paid - Billing mixin
    if (field.id === InvoicesFormFields.PAID) {
      ;(this as any).paymentSwitchLogic(schema, formData, field, value)
    }
  }

  showDialogConvertToCustomerInvoice() {
    const proformaInvoice = this.selectedRegisterData(this.getContext)
    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.convert_to_customer_invoice', { subject: proformaInvoice.description }),
      action: this.convertToCustomerInvoice,
      mainButtonText: this.$t('action_buttons.convert_to_customer_invoice')
    })
  }

  showDialogRectifyInvoice() {
    const invoice = this.selectedRegisterData(this.getContext)
    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.rectify_customer_invoice_text', {
        register: invoice.code
      }),
      action: () => this.rectifyCustomerInvoice(invoice.id)
    })
  }

  async rectifyCustomerInvoice(selectedInvoiceId: number) {
    const returnedId = await this.rectifyCustomerInvoiceAction(selectedInvoiceId)
    const routeData = this.$router.resolve({
      name: `${URLS.BILLING}-${URLS.CUSTOMER_INVOICES}`,
      params: { selectedRegisterId: returnedId }
    })

    window.open(routeData.href, '_blank')
  }

  async convertToCustomerInvoice() {
    try {
      const newCustomerInvoiceId = await this.convertProformaToCustomerInvoice(this.selectedInvoiceId)

      this.showAlert({
        type: AlertsTypes.SUCCESS,
        message: this.$t('components.alerts.convert_to_customer_invoice_success'),
        componentWhereIsRendered: ComponentWhereIsRendered.TABS_VIEW
      })

      const routeData = this.$router.resolve({
        name: `${URLS.BILLING}-${URLS.CUSTOMER_INVOICES}`,
        params: { selectedRegisterId: newCustomerInvoiceId }
      })

      window.open(routeData.href, '_blank')

      this.componentKey += 1
    } catch (error) {
      this.showDialogAction({
        type: DialogTypes.ERROR,
        message: this.$t('components.dialog.convert_to_customer_invoice_error')
      })
    }
  }

  setNewRegisterBreadcrumbLiteral() {
    const contextToTranslationKeyMap: Record<string, string> = {
      [ContextName.BILLING_PROVISIONS]: 'views.selected_register.new_provision',
      [ContextName.BILLING_ORDER_FORMS]: 'views.selected_register.new_order_form',
      [ContextName.BILLING_PROFORMA_INVOICES]: 'views.selected_register.new_proforma_invoice',
      [ContextName.BILLING_CUSTOMER_INVOICES]: 'views.selected_register.new_customer_invoice',
      [ContextName.BILLING_PROVIDER_INVOICES]: 'views.selected_register.new_provider_invoice'
    }

    const translationKey = contextToTranslationKeyMap[this.context] || 'views.selected_register.new_invoice'
    return this.$t(translationKey)
  }

  remove() {
    this.showDialog({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.remove_register_text', {
        text: this.setDialogText,
        register: this.getSelectedRegisterData[this.entity(this.getContext).keyName]
      }),
      action: this.removeRegister,
      mainButtonText: this.$t('action_buttons.remove'),
      secondaryButtonText: this.$t('action_buttons.cancel')
    })
  }

  async generateTemplateDialog(templateSelected: any) {
    await printInvoice(templateSelected, this.entityId, this.entityType)
    this.showTemplateDialog = false
  }

  checkIfOnlyOneInvoice(): boolean {
    if ((this.tree && this.tree.length === 0) || this.tree.length > 1) {
      return true
    }
    const template: ITemplate = this.tree[0]
    if (!template.isDirectory) {
      this.generateTemplateDialog(template)
      return false
    }
    return true
  }

  async checkIfComesFromExpedient() {
    await this.$nextTick()
    if (
      this.routeFromName === `${URLS.EXPEDIENTS}-${URLS.EXPEDIENTS}` &&
      this.getContext === ContextName.BILLING_ORDER_FORMS
    ) {
      Vue.set(this.getFormData, InvoicesFormFields.EXPEDIENT_ID, this.selectedRegisterId(ContextName.EXPEDIENTS))
      if (this.expedientCustomerFactGetter) {
        Vue.set(this.getFormData, InvoicesFormFields.EXPEDIENT_CUSTOMER_FACT, this.expedientCustomerFactGetter)
      }
    }
  }

  buildSendEmailItem() {
    if (this.isFactureUser) {
      return []
    }
    if (this.isFacturaEIssuedView && this.isFacturaEStatusError) {
      return []
    }

    return [
      {
        icon: Icons.EMAIL,
        tooltip: this.$t('action_buttons.send_by_email'),
        action: ActionName.SEND_EMAIL,
        enabled: this.showSendEmailButton,
        hidden: this.isProviderInvoicesTabView || this.isCustomerBankdraftsTabView || this.isProviderBankdraftsTabView,
        isContextMenuOption: true
      }
    ]
  }

  buildRectifyInvoiceItem() {
    if (!this.isCustomerInvoiceTabView && !this.isFacturaEIssuedView) {
      return []
    }
    return [
      {
        icon: Icons.RECTIFY_CUSTOMER_INVOICE,
        tooltip: this.$t('components.context_menu.rectify_customer_invoice'),
        action: ActionName.RECTIFY_CUSTOMER_INVOICE,
        enabled: this.showRectifyButton,
        isContextMenuOption: true
      }
    ]
  }
}
</script>

<style lang="scss">
.proformainvoicesgeneraldataform,
.lg {
  .fieldset .form-row .lexon-button-container.form-element.xl .lexon-button {
    top: 0;
  }
}
</style>
